/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JSX } from 'react';
import React from 'react';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { getIn } from 'formik';
import type { AnyAction, Dispatch } from 'redux';

import { withStore } from '../../store';
import {
  updateField,
  updatePropertyValue
} from '../../store/customer';
import { clearFieldError } from '../../store/fieldError';
import { getCardAndAttributeByName } from '../../utils/addressHelpers';
import InputProvider from '../InputProvider';

import './index.css';

function updateDependentPicklist(
  value: any,
  name: string,
  apiService: any,
  dispatch: (action: any) => void
) {
  const { cardId, index, attribute } =
    getCardAndAttributeByName(name);

  if (attribute === 'country') {
    apiService.getOdataList(dispatch, cardId, index, value);
  }
}

interface SelectOption {
  value: string;
  label: string;
  Comp?: React.ComponentType<{ label: string }>;
}

interface SelectDropDownProps {
  placeholder?: string;
  label?: string;
  required?: boolean;
  options: SelectOption[];
  onChange?: (value: string) => void;
  value?: string;
  dispatch: Dispatch<AnyAction>;
  store: any;
  name: string;
  useValueProp?: boolean;
  apiService: any;
  formik?: any;
}

const Component = ({
  placeholder = 'Select',
  label,
  required,
  options,
  onChange,
  value,
  dispatch,
  store,
  name,
  useValueProp,
  apiService,
  formik
}: SelectDropDownProps): JSX.Element => {
  const handleChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const {
      target: { value }
    } = event;

    const currentValue = formik
      ? getIn(formik.form.values, name)
      : getIn(store.customer, name);

    if (value === currentValue) {
      return;
    }

    if (onChange) {
      onChange(value);
    }

    updateDependentPicklist(value, name, apiService, dispatch);
    dispatch(updateField({ name, value }));

    const updatedCustomer =
      updatePropertyValue(
        { ...store.customer },
        name,
        value
      );

    await apiService.updateCustomer(
      updatedCustomer,
      dispatch
    );

    if (formik) {
      if (getIn(formik.form.errors, name)) {
        dispatch(clearFieldError(name));
      }
      formik.form.setFieldValue(name, value);
    }
  };

  const formikValue = formik
    ? getIn(formik.form.values, name)
    : undefined;

  const localValue = useValueProp
    ? value
    : formikValue ?? getIn(store.customer, name);

  return (
    <InputProvider
      formik={formik}
      name={name}
      dispatch={dispatch}
      store={store}
      apiService={apiService}
    >
      {({ hasError, isTouched }: any) => (
        <Box
          sx={{
            width: '100%',
            mb: '16px',
            paddingTop: '4px'
          }}
        >
          <TextField
            select
            variant="outlined"
            fullWidth
            sx={{ width: '100%', minWidth: '100%' }}
            error={hasError && isTouched}
            required={!!required}
            name={name}
            value={localValue || ''}
            onChange={handleChange}
            label={label || undefined}
            slotProps={{
              select: {
                displayEmpty: true,
                sx: {
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  textOverflow: 'unset',
                  overflow: 'visible'
                },
                renderValue: (selected: any) => {
                  if (!selected) {
                    return placeholder;
                  }
                  const match = options.find(
                    opt =>
                      opt.value?.toLowerCase() ===
                      String(selected).toLowerCase()
                  );
                  return match?.label ?? placeholder;
                },
                MenuProps: {
                  PaperProps: {
                    sx: {
                      maxHeight: 320,
                      overflowY: 'auto'
                    }
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                  }
                }
              }
            }}
          >
            {options.map(option => {
              const { Comp, value, label } = option;

              return (
                <MenuItem
                  key={value}
                  value={value}
                  sx={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere'
                  }}
                >
                  {Comp ? <Comp label={label} /> : label}
                </MenuItem>
              );
            })}
          </TextField>
        </Box>
      )}
    </InputProvider>
  );
};

const SelectDropDown = withStore(Component);

export default SelectDropDown;